import { SelectedPaymentOptions } from '@reward-platform/order-schemas/form-data'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useDefaultPaymentOptions } from '~/hooks/useDefaultPaymentOptions/useDefaultPaymentOptions'
import { PlatformError } from '~/utils/errors'
import { BasketItemType } from '@reward-platform/order-schemas/basket'
import { HotelItemType } from '@reward-platform/order-schemas/common'
import { StorageKeys, useStateStorage } from '@reward-platform/utils/client'
import { useBasket } from '~/hooks/useBasket'
import usePaymentOptionsStorage from '../hooks/storage/usePaymentOptions'

type PaymentOptionChangeListener = {
  id: string
  listener: (paymentOptions: SelectedPaymentOptions) => void
}

export type SelectedBalanceBoosterOption = {
  id: string
  price: number
  aviosAmount: number
  balanceBoosterCode: string
}

type SelectedBalanceBoosterOptions = Partial<Record<BasketItemType, SelectedBalanceBoosterOption>>

const parseSelectedBalanceBoosterOptions = (value: unknown): SelectedBalanceBoosterOptions => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return {}
  }

  const option = (value as Record<string, unknown>)[HotelItemType]
  if (typeof option !== 'object' || option === null || Array.isArray(option)) {
    return {}
  }

  const { id, price, aviosAmount, balanceBoosterCode } = option as Record<string, unknown>
  if (
    typeof id !== 'string' ||
    typeof price !== 'number' ||
    typeof aviosAmount !== 'number' ||
    typeof balanceBoosterCode !== 'string'
  ) {
    return {}
  }

  return {
    [HotelItemType]: { id, price, aviosAmount, balanceBoosterCode },
  }
}

type PaymentOptionsContextValue = {
  paymentOptions: SelectedPaymentOptions | undefined
  selectedBalanceBoosterOptions: SelectedBalanceBoosterOptions
  setBalanceBoosterForItem: (itemType: BasketItemType, balanceBoosterCode: string | null) => void
  setBalanceBoosterOptionForItem: (
    itemType: BasketItemType,
    option: SelectedBalanceBoosterOption | null
  ) => void
  removeBalanceBoosterForItem: (itemType: BasketItemType) => void
  setPaymentOptions: (paymentOptions: SelectedPaymentOptions) => void
  addPaymentOptionChangeListener: (listener: PaymentOptionChangeListener) => void
  removePaymentOptionChangeListener: (id: string) => void
  paymentOptionChangeListeners: PaymentOptionChangeListener[]
}

const PaymentOptionsContext = createContext<PaymentOptionsContextValue | undefined>(undefined)

type PaymentOptionsProviderProps = PropsWithChildren<{
  useStorage?: boolean
  onChangePaymentOptions?: (paymentOptions: SelectedPaymentOptions) => void
}>

type UsePaymentOptionsParams = {
  useStorage: boolean
}

type UsePaymentOptionsReturn = {
  paymentOptions: SelectedPaymentOptions | undefined
  setPaymentOptions: (paymentOptions: SelectedPaymentOptions) => void
}

const handleBasketChanges = (
  basketItemsIds: string[],
  previousBasketItemsIdsRef: React.MutableRefObject<string[]>,
  hasSetDefaultsRef: React.MutableRefObject<boolean>
) => {
  const basketItemsChanged =
    previousBasketItemsIdsRef.current.length !== basketItemsIds.length ||
    previousBasketItemsIdsRef.current.some((id) => !basketItemsIds.includes(id))

  if (basketItemsChanged) {
    const newItemsAdded = basketItemsIds.some(
      (id) => !previousBasketItemsIdsRef.current.includes(id)
    )
    previousBasketItemsIdsRef.current = basketItemsIds
    // Reset defaults when new items are added. When items are removed the parser
    // strips the removed items data and the remaining selections stays valid in storage
    if (newItemsAdded) {
      hasSetDefaultsRef.current = false
    }
  }
}

const applyDefaultPaymentOptions = (
  hasSetDefaultsRef: React.MutableRefObject<boolean>,
  defaultPaymentOptions: SelectedPaymentOptions | undefined,
  isHydrated: boolean,
  useStorage: boolean,
  setStorage: (options: SelectedPaymentOptions) => void,
  setPaymentOptions: (options: SelectedPaymentOptions) => void
) => {
  // Set default payment options when ready and not already set for current basket
  if (!hasSetDefaultsRef.current && defaultPaymentOptions && isHydrated) {
    hasSetDefaultsRef.current = true
    if (useStorage) {
      setStorage(defaultPaymentOptions)
    } else {
      setPaymentOptions(defaultPaymentOptions)
    }
  }
}

const usePaymentOptions = ({ useStorage }: UsePaymentOptionsParams): UsePaymentOptionsReturn => {
  const defaultPaymentOptions = useDefaultPaymentOptions()
  const { storage, setStorage, isHydrated } = usePaymentOptionsStorage(true)
  const [paymentOptions, setPaymentOptions] = useState<SelectedPaymentOptions>()

  const {
    basket: { data: basket },
  } = useBasket()

  const basketItemsIds = useMemo(
    () =>
      Object.values(basket?.items ?? {})
        .flat()
        .flatMap((item) => (item ? [`${item.type}-${item.id}`] : [])),
    [basket]
  )

  const previousBasketItemsIdsRef = useRef<string[]>([])
  const hasSetDefaultsRef = useRef(false)

  useEffect(() => {
    handleBasketChanges(basketItemsIds, previousBasketItemsIdsRef, hasSetDefaultsRef)
    applyDefaultPaymentOptions(
      hasSetDefaultsRef,
      defaultPaymentOptions,
      isHydrated,
      useStorage,
      setStorage,
      setPaymentOptions
    )
  }, [basketItemsIds, defaultPaymentOptions, isHydrated, setStorage, useStorage])

  if (useStorage) {
    return {
      paymentOptions: storage,
      setPaymentOptions: setStorage,
    }
  }

  return {
    paymentOptions,
    setPaymentOptions,
  }
}

export const usePaymentOptionsContext = () => {
  const context = useContext(PaymentOptionsContext)

  if (!context) {
    throw new PlatformError('usePaymentOptionsContext must be used within PaymentOptionsProvider')
  }

  return context
}

export const PaymentOptionsProvider = ({
  children,
  onChangePaymentOptions,
  useStorage,
}: PaymentOptionsProviderProps) => {
  const { paymentOptions, setPaymentOptions: setStoredPaymentOptions } = usePaymentOptions({
    useStorage: Boolean(useStorage),
  })
  const [inMemoryBalanceBoosterOptions, setInMemoryBalanceBoosterOptions] =
    useState<SelectedBalanceBoosterOptions>({})
  const { storage: storedBalanceBoosterOptions, setStorage: setStoredBalanceBoosterOptions } =
    useStateStorage<SelectedBalanceBoosterOptions>({
      key: StorageKeys.BASKET_BALANCE_BOOSTER_OPTIONS,
      parser: parseSelectedBalanceBoosterOptions,
    })
  const selectedBalanceBoosterOptions = useStorage
    ? storedBalanceBoosterOptions
    : inMemoryBalanceBoosterOptions

  const [paymentOptionChangeListeners, setPaymentOptionChangeListeners] = useState<
    PaymentOptionChangeListener[]
  >([])

  const setPaymentOptions = useCallback(
    (updatedPaymentOptions: SelectedPaymentOptions) => {
      setStoredPaymentOptions(updatedPaymentOptions)
      onChangePaymentOptions?.(updatedPaymentOptions)
      paymentOptionChangeListeners.forEach((listener) => listener.listener(updatedPaymentOptions))
    },
    [onChangePaymentOptions, setStoredPaymentOptions, paymentOptionChangeListeners]
  )

  const setBalanceBoosterOptionForItem = useCallback(
    (itemType: BasketItemType, option: SelectedBalanceBoosterOption | null) => {
      const updatedOptions = { ...selectedBalanceBoosterOptions }
      if (option) {
        updatedOptions[itemType] = option
      } else {
        delete updatedOptions[itemType]
      }

      if (useStorage) {
        setStoredBalanceBoosterOptions(updatedOptions)
      } else {
        setInMemoryBalanceBoosterOptions(updatedOptions)
      }
    },
    [selectedBalanceBoosterOptions, setStoredBalanceBoosterOptions, useStorage]
  )

  const setBalanceBoosterForItem = useCallback(
    (itemType: BasketItemType, balanceBoosterCode: string | null) => {
      setBalanceBoosterOptionForItem(itemType, null)
      setPaymentOptions({
        ...paymentOptions,
        [itemType]: {
          ...paymentOptions?.[itemType],
          balanceBooster: balanceBoosterCode,
        },
      })
    },
    [paymentOptions, setBalanceBoosterOptionForItem, setPaymentOptions]
  )

  const removeBalanceBoosterForItem = useCallback(
    (itemType: BasketItemType) => setBalanceBoosterForItem(itemType, null),
    [setBalanceBoosterForItem]
  )

  const addPaymentOptionChangeListener = useCallback((listener: PaymentOptionChangeListener) => {
    setPaymentOptionChangeListeners((prev) => [...prev, listener])
  }, [])

  const removePaymentOptionChangeListener = useCallback((id: string) => {
    setPaymentOptionChangeListeners((prev) => prev.filter((listener) => listener.id !== id))
  }, [])

  const contextValue = useMemo<PaymentOptionsContextValue>(
    () => ({
      paymentOptions,
      selectedBalanceBoosterOptions,
      setPaymentOptions,
      removeBalanceBoosterForItem,
      setBalanceBoosterForItem,
      setBalanceBoosterOptionForItem,
      addPaymentOptionChangeListener,
      removePaymentOptionChangeListener,
      paymentOptionChangeListeners,
    }),
    [
      addPaymentOptionChangeListener,
      paymentOptionChangeListeners,
      paymentOptions,
      removeBalanceBoosterForItem,
      removePaymentOptionChangeListener,
      selectedBalanceBoosterOptions,
      setBalanceBoosterOptionForItem,
      setBalanceBoosterForItem,
      setPaymentOptions,
    ]
  )

  return (
    <PaymentOptionsContext.Provider value={contextValue}>{children}</PaymentOptionsContext.Provider>
  )
}
